import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchCihazDegisimiRandevulari, createCihazDegisimi } from '../services/cihazDegisimiService';
import { toValidObjectIdString, parseApiDate } from '../utils/security';
import TableListFooter from './TableListFooter';
import './CihazDegisimiOlusturModal.css';

const RANDEVU_PAGE_LIMIT = 5;
const SEARCH_DEBOUNCE_MS = 320;

const getRandevuId = (r) => toValidObjectIdString(r?.id ?? r?._id) || '';
const getCihazId = (c) => toValidObjectIdString(c?.id ?? c?._id) || '';

/** Tarih: 01 Haziran 2026 — saat ayrı alandan (randevuSaati) */
const formatRandevuTarihGosterim = (randevuTarihi, randevuSaati) => {
  const d = parseApiDate(randevuTarihi);
  if (!d || isNaN(d.getTime())) {
    return { tarih: '—', saat: randevuSaati ? String(randevuSaati).trim() : '' };
  }
  const tarih = d.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const saat = randevuSaati != null && String(randevuSaati).trim() ? String(randevuSaati).trim() : '';
  return { tarih, saat };
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const CihazDegisimiOlusturModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [randevular, setRandevular] = useState([]);
  const [selectedRandevu, setSelectedRandevu] = useState(null);
  const [selectedCihaz, setSelectedCihaz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [arama, setArama] = useState('');
  const [debouncedArama, setDebouncedArama] = useState('');
  const [sayfa, setSayfa] = useState(1);
  const [sayfalama, setSayfalama] = useState({
    toplam: 0,
    toplamSayfa: 0,
    mevcutSayfa: 1,
    limit: RANDEVU_PAGE_LIMIT,
  });
  const initialSelectDone = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedArama(arama.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [arama]);

  useEffect(() => {
    setSayfa(1);
    initialSelectDone.current = false;
  }, [debouncedArama]);

  const loadRandevular = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetchCihazDegisimiRandevulari({
        arama: debouncedArama,
        sayfa,
        limit: RANDEVU_PAGE_LIMIT,
      });
      const rows = response?.veri?.randevular || [];
      const pg = response?.veri?.sayfalama || {};
      setRandevular(rows);
      setSayfalama({
        toplam: pg.toplam ?? 0,
        toplamSayfa: pg.toplamSayfa ?? 0,
        mevcutSayfa: pg.mevcutSayfa ?? sayfa,
        limit: pg.limit ?? RANDEVU_PAGE_LIMIT,
      });

      setSelectedRandevu((prev) => {
        const prevId = getRandevuId(prev);
        if (prevId && rows.some((r) => getRandevuId(r) === prevId)) return prev;
        if (rows.length > 0) {
          initialSelectDone.current = true;
          return rows[0];
        }
        return null;
      });
    } catch (err) {
      setLoadError(getErrorMessage(err, 'Randevular yüklenemedi'));
      setRandevular([]);
      setSelectedRandevu(null);
    } finally {
      setLoading(false);
    }
  }, [debouncedArama, sayfa]);

  useEffect(() => {
    if (step === 1) {
      loadRandevular();
    }
  }, [step, loadRandevular]);

  const cihazlar = selectedRandevu?.cihazlar || [];

  const handleRandevuSelect = (r) => {
    setSelectedRandevu(r);
    setSelectedCihaz(null);
    setError('');
  };

  const handleCihazSelect = (c) => {
    setSelectedCihaz(c);
    setError('');
  };

  const handleSubmit = async () => {
    const randevuId = getRandevuId(selectedRandevu);
    const cihazId = getCihazId(selectedCihaz);

    if (!randevuId || !cihazId) {
      setError('Randevu ve cihaz seçimi zorunludur');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const response = await createCihazDegisimi({ randevuId, cihazId });
      if (response?.basarili) {
        window.showNotification?.('success', response?.mesaj || 'Cihaz değişimi talebi oluşturuldu');
        onSuccess?.();
        onClose();
      } else {
        setError(response?.mesaj || 'Talep oluşturulamadı');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Talep oluşturulurken hata oluştu'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedCihaz(null);
      setError('');
    } else {
      onClose();
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!selectedRandevu) {
        setError('Lütfen bir randevu seçin');
        return;
      }
      setError('');
      setStep(2);
    }
  };

  const totalPages = Math.max(1, sayfalama.toplamSayfa || 1);

  return (
    <div className="modal-overlay cihaz-degisimi-olustur-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className="fas fa-exchange-alt" style={{ marginRight: '8px' }}></i>
            Cihaz Değişimi Oluştur
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="cihaz-degisimi-steps">
            <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>1</div>
            <div className="step-line" />
            <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>2</div>
            <span className="step-label step-1">Randevu Seç</span>
            <span className="step-label step-2">Cihaz Seç</span>
          </div>

          {error && (
            <div className="modal-error" role="alert">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="cihaz-degisimi-step-content">
              <p className="step-desc">Tamamlanmış randevulardan birini seçin (sadece cihazı olan randevular listelenir):</p>

              <div className="cihaz-degisimi-randevu-toolbar">
                <div className="cihaz-degisimi-randevu-search">
                  <i className="fas fa-search" aria-hidden />
                  <input
                    type="search"
                    className="cihaz-degisimi-randevu-search-input"
                    placeholder="Randevu numarası veya müşteri adı ile ara..."
                    value={arama}
                    onChange={(e) => setArama(e.target.value)}
                    aria-label="Randevu numarası veya müşteri adı ile ara"
                  />
                  {arama && (
                    <button
                      type="button"
                      className="cihaz-degisimi-randevu-search-clear"
                      onClick={() => setArama('')}
                      aria-label="Aramayı temizle"
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
              </div>

              {loadError && (
                <div className="modal-error" role="alert">
                  {loadError}
                </div>
              )}

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <div>Randevular yükleniyor</div>
                </div>
              ) : randevular.length === 0 ? (
                <div className="empty-state">
                  {debouncedArama
                    ? 'Aramanızla eşleşen randevu bulunamadı'
                    : 'Uygun randevu bulunamadı (Tamamlanmış randevularda cihaz olan kayıtlar listelenir)'}
                </div>
              ) : (
                <>
                  <div className="randevu-list">
                    {randevular.map((r) => {
                      const selected = getRandevuId(selectedRandevu) === getRandevuId(r);
                      const { tarih, saat } = formatRandevuTarihGosterim(r.randevuTarihi, r.randevuSaati);
                      return (
                        <button
                          key={getRandevuId(r)}
                          type="button"
                          className={`randevu-item${selected ? ' selected' : ''}`}
                          onClick={() => handleRandevuSelect(r)}
                          aria-pressed={selected}
                        >
                          <span className="randevu-no">{r.randevuNumarasi || '—'}</span>
                          {r.musteriAdi ? (
                            <span className="randevu-musteri">{r.musteriAdi}</span>
                          ) : null}
                          <span className="randevu-meta">
                            {tarih}
                            {saat ? <span className="randevu-saat"> · {saat}</span> : null}
                          </span>
                          <span className="randevu-cihaz-sayisi">{(r.cihazlar || []).length} cihaz</span>
                        </button>
                      );
                    })}
                  </div>
                  <TableListFooter
                    variant="compact"
                    embedded
                    className="cihaz-degisimi-randevu-footer"
                    totalRecords={sayfalama.toplam ?? 0}
                    currentPage={sayfalama.mevcutSayfa || sayfa}
                    totalPages={totalPages}
                    onPageChange={setSayfa}
                    loading={loading}
                  />
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="cihaz-degisimi-step-content">
              <p className="step-desc">
                Randevu: <strong>{selectedRandevu?.randevuNumarasi}</strong>
                {selectedRandevu?.musteriAdi ? (
                  <> — <strong>{selectedRandevu.musteriAdi}</strong></>
                ) : null}
                {' '}— Değiştirilecek cihazı seçin:
              </p>
              {cihazlar.length === 0 ? (
                <div className="empty-state">Bu randevuda cihaz bulunamadı</div>
              ) : (
                <div className="cihaz-list">
                  {cihazlar.map((c) => {
                    const selected = getCihazId(selectedCihaz) === getCihazId(c);
                    return (
                      <button
                        key={getCihazId(c)}
                        type="button"
                        className={`cihaz-item${selected ? ' selected' : ''}`}
                        onClick={() => handleCihazSelect(c)}
                        aria-pressed={selected}
                      >
                        <span className="cihaz-seri">{c.seriNumarasi || '—'}</span>
                        <span className="cihaz-urun">{c.urunAdi || '—'}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={handleBack}>
              {step === 1 ? 'İptal' : 'Geri'}
            </button>
            {step === 1 ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
                disabled={!selectedRandevu || loading}
              >
                İleri
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!selectedCihaz || submitting}
              >
                {submitting ? 'Gönderiliyor...' : 'Talep Oluştur'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CihazDegisimiOlusturModal;
